import React, { useState, useRef } from 'react';
import { useFormikContext } from 'formik';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Popover, Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fr } from 'date-fns/locale';
import * as Styled from 'eCoach/_styles/FormBuilder.style';
import Switch from 'shared/_styles/Switch.style';
import { Placeholder } from 'types/step';
import dayjs from 'shared/helpers/dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Arrow, Time, General } from 'icons/untitledui';

dayjs.extend(customParseFormat);

interface FormInputProps {
  label: string;
  readonly?: boolean;
  fieldKey: string;
  inputType: Placeholder['type'];
  options?: string[];
  format?: string;
}

export default function FormInput({
  label,
  readonly,
  fieldKey,
  inputType,
  options,
  format,
}: FormInputProps) {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const { i18n, t } = useTranslation('pageEcoach');
  const locale = i18n.language === 'fr' ? fr : undefined;
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(
    null
  );
  const [calendarView, setCalendarView] = useState<'year' | 'month' | 'day'>(
    'day'
  );
  const [multidateCalendarAnchor, setMultidateCalendarAnchor] =
    useState<HTMLElement | null>(null);
  const multidateActiveIndexRef = useRef<number>(0);
  const [multidateInputCount, setMultidateInputCount] = useState<number>(1);
  const [dateTextListCalendarAnchor, setDateTextListCalendarAnchor] =
    useState<HTMLElement | null>(null);
  const dateTextListActiveIndexRef = useRef<number>(0);
  const [dateTextListInputCount, setDateTextListInputCount] =
    useState<number>(1);

  const dateFormat = format || t('FormFilling.dateFormat');
  const datePlaceholder = t('FormFilling.datePlaceholder');
  const textPlaceholder = t('FormFilling.textPlaceholder');

  const handleChange = (value: boolean | number | string | Date) => {
    setFieldValue(fieldKey, value);
  };

  const handleCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarAnchor(event.currentTarget);
    setCalendarView('day');
  };

  const handleCalendarClose = () => {
    setCalendarAnchor(null);
  };

  const handleNumberStep = (negative: boolean = false) => {
    const currentValue = Number(values[fieldKey]) || 0;
    const step = inputType === 'float' ? 0.01 : 1;
    const newValue = currentValue + step * (negative ? -1 : 1);
    const formattedValue =
      inputType === 'float' ? newValue.toFixed(2) : String(newValue);
    handleChange(formattedValue);
  };

  const handleMultidateCalendarClose = () => {
    setMultidateCalendarAnchor(null);
  };

  const handleDateTextListCalendarClose = () => {
    setDateTextListCalendarAnchor(null);
  };

  const getInputComponent = () => {
    if (inputType === 'boolean') {
      return (
        <Styled.SwitchWrapper>
          <Switch
            id={fieldKey}
            checked={Boolean(values[fieldKey])}
            onChange={(e) => handleChange(e.target.checked)}
          />
        </Styled.SwitchWrapper>
      );
    }
    if (inputType === 'date') {
      return (
        <>
          <Styled.DateInputWrapper>
            <Styled.DateInputField
              id={fieldKey}
              name={fieldKey}
              type="text"
              value={values[fieldKey] || ''}
              onChange={(e) => handleChange(e.target.value)}
              onClick={handleCalendarOpen}
              placeholder={datePlaceholder}
            />
            <Styled.DateInputButton
              type="button"
              onClick={handleCalendarOpen}
              aria-label="Open calendar"
            >
              <Time.CalenderDate />
            </Styled.DateInputButton>
          </Styled.DateInputWrapper>
          <Popover
            open={Boolean(calendarAnchor)}
            anchorEl={calendarAnchor}
            onClose={handleCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={locale}
            >
              <DateCalendar
                value={(() => {
                  const inputValue = values[fieldKey];
                  if (!inputValue) return null;

                  const formats = [
                    'D MMMM',
                    'DD MMMM',
                    'D MMMM YYYY',
                    'DD MMMM YYYY',
                    'MMMM D, YYYY',
                    'DD-MM-YYYY',
                    'YYYY-MM-DD',
                    'MM/DD/YYYY',
                    'DD/MM/YYYY',
                    'M/D/YYYY',
                    'D/M/YYYY',
                  ];

                  const strictParsedDate = dayjs(inputValue, formats, true);
                  const parsedDate = strictParsedDate.isValid()
                    ? strictParsedDate
                    : dayjs(inputValue);

                  if (!parsedDate.isValid()) return null;

                  const hasYear = /\d{4}/.test(inputValue);
                  if (!hasYear) {
                    return parsedDate.year(dayjs().year()).toDate();
                  }

                  return parsedDate.toDate();
                })()}
                view={calendarView}
                onViewChange={(newView) => setCalendarView(newView)}
                onChange={(date) => {
                  if (date) {
                    const formattedDate = dayjs(date).format(dateFormat);
                    handleChange(formattedDate);
                    // Only close if we're in day view (user clicked a day)
                    if (calendarView === 'day') {
                      setCalendarAnchor(null);
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Popover>
        </>
      );
    }
    if (inputType === 'multidate') {
      const MULTIDATE_SEPARATOR = ' | ';

      const parseStoredDates = (): string[] => {
        const stored = values[fieldKey];
        if (!stored) return [];
        return String(stored).split(MULTIDATE_SEPARATOR);
      };

      const storedDates = parseStoredDates();
      const inputCount = Math.max(multidateInputCount, storedDates.length, 1);
      const displayDates: string[] = [];
      for (let i = 0; i < inputCount; i += 1) {
        displayDates.push(storedDates[i] || '');
      }

      const saveDates = (newDates: string[]) => {
        // Don't filter empty strings - preserve positions during editing
        handleChange(newDates.join(MULTIDATE_SEPARATOR));
      };

      const handleDateInputChange = (index: number, value: string) => {
        if (value.includes('|')) {
          return;
        }
        const newDates = [...displayDates];
        newDates[index] = value;
        saveDates(newDates);
      };

      const handleCalendarSelect = (date: Date | null) => {
        if (date) {
          const formattedDate = dayjs(date).format(dateFormat);
          const newDates = [...displayDates];
          newDates[multidateActiveIndexRef.current] = formattedDate;
          saveDates(newDates);
          // Only close if we're in day view (user clicked a day)
          if (calendarView === 'day') {
            setMultidateCalendarAnchor(null);
          }
        }
      };

      const handleAddDateInput = () => {
        setMultidateInputCount(inputCount + 1);
      };

      const handleRemoveDateInput = (index: number) => {
        const newDates = displayDates.filter((_, i) => i !== index);
        saveDates(newDates);
        setMultidateInputCount(Math.max(inputCount - 1, 1));
      };

      return (
        <Styled.MultidateWrapper>
          {displayDates.map((dateValue, index) => (
            <Styled.MultidateDateRow key={index}>
              <Styled.DateInputWrapper style={{ flex: 1 }}>
                <Styled.DateInputField
                  type="text"
                  value={dateValue}
                  onChange={(e) => handleDateInputChange(index, e.target.value)}
                  placeholder={datePlaceholder}
                />
                <Styled.DateInputButton
                  type="button"
                  onClick={(e) => {
                    multidateActiveIndexRef.current = index;
                    setCalendarView('day');
                    setMultidateCalendarAnchor(e.currentTarget);
                  }}
                  aria-label="Open calendar"
                >
                  <Time.CalenderDate />
                </Styled.DateInputButton>
              </Styled.DateInputWrapper>
              {inputCount > 1 && (
                <Styled.RemoveDateButton
                  type="button"
                  onClick={() => handleRemoveDateInput(index)}
                  aria-label="Remove date"
                >
                  <General.XClose />
                </Styled.RemoveDateButton>
              )}
            </Styled.MultidateDateRow>
          ))}
          <Styled.AddDateButton
            type="button"
            onClick={handleAddDateInput}
            aria-label="Add date"
          >
            <General.Plus />
          </Styled.AddDateButton>
          <Popover
            open={Boolean(multidateCalendarAnchor)}
            anchorEl={multidateCalendarAnchor}
            onClose={handleMultidateCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={locale}
            >
              <DateCalendar
                value={(() => {
                  const activeInputValue =
                    displayDates[multidateActiveIndexRef.current];
                  if (!activeInputValue) return null;

                  const formats = [
                    'D MMMM',
                    'DD MMMM',
                    'D MMMM YYYY',
                    'DD MMMM YYYY',
                    'MMMM D, YYYY',
                    'DD-MM-YYYY',
                    'YYYY-MM-DD',
                    'MM/DD/YYYY',
                    'DD/MM/YYYY',
                    'M/D/YYYY',
                    'D/M/YYYY',
                  ];

                  const strictParsedDate = dayjs(
                    activeInputValue,
                    formats,
                    true
                  );
                  const parsedDate = strictParsedDate.isValid()
                    ? strictParsedDate
                    : dayjs(activeInputValue);

                  if (!parsedDate.isValid()) return null;

                  const hasYear = /\d{4}/.test(activeInputValue);
                  if (!hasYear) {
                    return parsedDate.year(dayjs().year()).toDate();
                  }

                  return parsedDate.toDate();
                })()}
                view={calendarView}
                onViewChange={(newView) => setCalendarView(newView)}
                onChange={handleCalendarSelect}
              />
            </LocalizationProvider>
          </Popover>
        </Styled.MultidateWrapper>
      );
    }
    if (inputType === 'dateTextList') {
      type DateTextItem = { date: string; text: string };

      const parseStoredItems = (): DateTextItem[] => {
        const stored = values[fieldKey];
        if (!stored || !Array.isArray(stored)) return [];
        return stored;
      };

      const storedItems = parseStoredItems();
      const inputCount = Math.max(
        dateTextListInputCount,
        storedItems.length,
        1
      );
      const displayItems: DateTextItem[] = [];
      for (let i = 0; i < inputCount; i += 1) {
        displayItems.push(storedItems[i] || { date: '', text: '' });
      }

      const saveItems = (newItems: DateTextItem[]) => {
        setFieldValue(fieldKey, newItems);
      };

      const handleDateChange = (index: number, date: string) => {
        const newItems = [...displayItems];
        newItems[index] = { ...newItems[index], date };
        saveItems(newItems);
      };

      const handleTextChange = (index: number, text: string) => {
        const newItems = [...displayItems];
        newItems[index] = { ...newItems[index], text };
        saveItems(newItems);
      };

      const handleCalendarSelect = (date: Date | null) => {
        if (date) {
          const formattedDate = dayjs(date).format(dateFormat);
          handleDateChange(dateTextListActiveIndexRef.current, formattedDate);
          if (calendarView === 'day') {
            setDateTextListCalendarAnchor(null);
          }
        }
      };

      const handleAddItem = () => {
        setDateTextListInputCount(inputCount + 1);
      };

      const handleRemoveItem = (index: number) => {
        const newItems = displayItems.filter((_, i) => i !== index);
        saveItems(newItems);
        setDateTextListInputCount(Math.max(inputCount - 1, 1));
      };

      return (
        <Styled.MultidateWrapper>
          {displayItems.map((item, index) => (
            <Styled.MultidateDateRow key={index}>
              <Styled.DateInputWrapper style={{ flex: 1 }}>
                <Styled.DateInputField
                  type="text"
                  value={item.date}
                  onChange={(e) => handleDateChange(index, e.target.value)}
                  placeholder={datePlaceholder}
                />
                <Styled.DateInputButton
                  type="button"
                  onClick={(e) => {
                    dateTextListActiveIndexRef.current = index;
                    setCalendarView('day');
                    setDateTextListCalendarAnchor(e.currentTarget);
                  }}
                  aria-label="Open calendar"
                >
                  <Time.CalenderDate />
                </Styled.DateInputButton>
              </Styled.DateInputWrapper>
              <Styled.DateTextInputField
                type="text"
                value={item.text}
                onChange={(e) => handleTextChange(index, e.target.value)}
                placeholder={textPlaceholder}
              />
              {inputCount > 1 && (
                <Styled.RemoveDateButton
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  aria-label="Remove item"
                >
                  <General.XClose />
                </Styled.RemoveDateButton>
              )}
            </Styled.MultidateDateRow>
          ))}
          <Styled.AddDateButton
            type="button"
            onClick={handleAddItem}
            aria-label="Add item"
          >
            <General.Plus />
          </Styled.AddDateButton>
          <Popover
            open={Boolean(dateTextListCalendarAnchor)}
            anchorEl={dateTextListCalendarAnchor}
            onClose={handleDateTextListCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={locale}
            >
              <DateCalendar
                value={(() => {
                  const activeItem =
                    displayItems[dateTextListActiveIndexRef.current];
                  const activeInputValue = activeItem?.date;
                  if (!activeInputValue) return null;

                  const formats = [
                    'D MMMM',
                    'DD MMMM',
                    'D MMMM YYYY',
                    'DD MMMM YYYY',
                    'MMMM D, YYYY',
                    'DD-MM-YYYY',
                    'YYYY-MM-DD',
                    'MM/DD/YYYY',
                    'DD/MM/YYYY',
                    'M/D/YYYY',
                    'D/M/YYYY',
                  ];

                  const strictParsedDate = dayjs(
                    activeInputValue,
                    formats,
                    true
                  );
                  const parsedDate = strictParsedDate.isValid()
                    ? strictParsedDate
                    : dayjs(activeInputValue);

                  if (!parsedDate.isValid()) return null;

                  const hasYear = /\d{4}/.test(activeInputValue);
                  if (!hasYear) {
                    return parsedDate.year(dayjs().year()).toDate();
                  }

                  return parsedDate.toDate();
                })()}
                view={calendarView}
                onViewChange={(newView) => setCalendarView(newView)}
                onChange={handleCalendarSelect}
              />
            </LocalizationProvider>
          </Popover>
        </Styled.MultidateWrapper>
      );
    }
    if (inputType === 'choice') {
      return (
        <Styled.ChoiceInputWrapper>
          <Autocomplete
            freeSolo
            disableClearable
            disablePortal
            options={options || []}
            value={values[fieldKey] || ''}
            onChange={(_, newValue) => {
              handleChange(newValue || '');
            }}
            onInputChange={(_, newInputValue) => {
              handleChange(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                id={fieldKey}
                name={fieldKey}
                variant="outlined"
              />
            )}
          />
        </Styled.ChoiceInputWrapper>
      );
    }
    if (inputType === 'number' || inputType === 'float') {
      return (
        <Styled.NumberInputWrapper>
          <Styled.NumberInputField
            id={fieldKey}
            name={fieldKey}
            type="text"
            value={values[fieldKey] || ''}
            onChange={(e) => handleChange(e.target.value)}
          />
          <Styled.NumberInputButtons>
            <Styled.NumberInputButton
              type="button"
              onClick={() => handleNumberStep()}
              aria-label="Increment"
            >
              <Arrow.ChevronUp />
            </Styled.NumberInputButton>
            <Styled.NumberInputButton
              type="button"
              onClick={() => handleNumberStep(true)}
              aria-label="Decrement"
            >
              <Arrow.ChevronDown />
            </Styled.NumberInputButton>
          </Styled.NumberInputButtons>
        </Styled.NumberInputWrapper>
      );
    }
    return (
      <Styled.InputField
        id={fieldKey}
        name={fieldKey}
        type="text"
        value={values[fieldKey] || ''}
        onChange={(e) => handleChange(e.target.value)}
      />
    );
  };

  const getDisplayValue = () => {
    const value = values[fieldKey] || '';
    if (inputType === 'multidate' && value) {
      // Clean up multidate display: filter empty dates and use ", "
      return String(value)
        .split(' | ')
        .map((d) => d.trim())
        .filter((d) => d)
        .join(', ');
    }
    if (inputType === 'dateTextList' && Array.isArray(value)) {
      // Format dateTextList display: "Date – Text" pairs separated by ", "
      return value
        .filter((item) => item?.date || item?.text)
        .map((item) => {
          const date = item.date || '';
          const text = item.text || '';
          return date && text ? `${date} – ${text}` : date || text;
        })
        .join(', ');
    }
    return value;
  };

  return (
    <Styled.InputContainer>
      <Styled.InputLabel htmlFor={fieldKey}>{label}</Styled.InputLabel>
      {readonly ? (
        <Styled.ReadonlyInputField>
          {getDisplayValue()}
        </Styled.ReadonlyInputField>
      ) : (
        getInputComponent()
      )}
    </Styled.InputContainer>
  );
}
